// SAFEBOX storage overlay — a placed safebox's private 20-slot / 200kg inventory (bank-vault style).
// Left: the safebox; right: your bag. Click an item in your bag to STORE it, or in the safebox to
// TAKE it. The server is authoritative (owner/staff/keyholder gated); this mirrors its snapshots.

import { INVENTORY_COLS, SAFEBOX_SLOTS, SAFEBOX_CAPACITY_KG, ITEM_WEIGHT_KG, type InventorySlot } from "@ghostland/shared";
import { inventoryStore } from "../items/inventoryStore.js";
import { itemThumb } from "../items/itemThumb.js";

export interface SafeboxActions {
  request: (buildingId: string, fid: string) => void;
  /** Move one unit bag → safebox; `to` = the drop-target box slot (omitted on click = auto-place). */
  store: (buildingId: string, fid: string, slotIndex: number, to?: number) => void;
  /** Move one unit safebox → bag; `to` = the drop-target bag slot (omitted on click = auto-place). */
  take: (buildingId: string, fid: string, slotIndex: number, to?: number) => void;
  /** Drag-reorder INSIDE the safebox (move/merge/swap — the bag's slot-to-slot semantics). */
  move: (buildingId: string, fid: string, from: number, to: number) => void;
  /** Drag-reorder INSIDE the bag grid (routes to the normal inventory MoveItem). */
  bagMove: (from: number, to: number) => void;
}
let actions: SafeboxActions | null = null;
export function setSafeboxActions(a: SafeboxActions): void { actions = a; }

const STYLE_ID = "ghostland-safebox-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-safe-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(0,0,0,.7); color: #e8e8e8;
}
#gl-safe-overlay, #gl-safe-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-safe-overlay.open { display: flex; }
#gl-safe-card { width: 520px; padding: 20px 18px 16px; background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated; }
#gl-safe-card h1 { margin: 0 0 2px; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
#gl-safe-card .hint { font-size: 7px; color: #7a7a7a; letter-spacing: 1px; margin-bottom: 14px; }
.gl-safe-cols { display: flex; gap: 18px; }
.gl-safe-col { flex: 1; }
.gl-safe-col h2 { margin: 0 0 8px; font-size: 8px; letter-spacing: 1px; color: #b0b0b0; }
.gl-safe-grid { display: grid; gap: 4px; }
.gl-safe-cell {
  width: 100%; aspect-ratio: 1; background: #0c0c0c; border: 1px solid #2a2a2a;
  display: flex; align-items: flex-end; justify-content: flex-end; position: relative; cursor: pointer;
}
.gl-safe-cell.empty { cursor: default; }
.gl-safe-cell:not(.empty):hover { border-color: #f4f4f4; }
.gl-safe-cell canvas { position: absolute; inset: 0; width: 100%; height: 100%; image-rendering: pixelated; }
.gl-safe-cell.drop-hint { border-color: var(--gl-accent, #b48cff); box-shadow: 0 0 0 2px rgba(180,140,255,.4) inset; }
.gl-safe-cell .q { position: relative; font-size: 8px; color: #f4f4f4; padding: 0 2px 1px; text-shadow: 1px 1px 0 #000; }
#gl-safe-bar { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; padding-top: 12px; border-top: 1px solid #1c1c1c; }
#gl-safe-kg { font-size: 8px; color: #8a8a8a; letter-spacing: 1px; }
#gl-safe-close { margin-top: 10px; width: 100%; padding: 7px 0; background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
#gl-safe-close:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
`;

let overlay: HTMLDivElement | null = null;
let boxGrid: HTMLDivElement | null = null;
let bagGrid: HTMLDivElement | null = null;
let boxTitle: HTMLHeadingElement | null = null;
let kgEl: HTMLDivElement | null = null;
let unsub: Array<() => void> = [];

// The open safebox (set by openSafebox; contents patched by setSafeboxSnapshot as the server pushes).
let current: { buildingId: string; fid: string } | null = null;
let slots: InventorySlot[] = [];

/** Route a drop landing on `target` grid slot `to` from the drag payload "src:index". */
function routeDrop(payload: string, target: "box" | "bag", to: number): void {
  if (!current) return;
  const [src, idxStr] = payload.split(":");
  const from = Number(idxStr);
  if (!Number.isInteger(from)) return;
  if (src === target) {
    if (from === to) return;
    if (target === "box") actions?.move(current.buildingId, current.fid, from, to); // reorder inside the box
    else actions?.bagMove(from, to);                                                // reorder inside the bag
  } else if (target === "box" && src === "bag") actions?.store(current.buildingId, current.fid, from, to);
  else if (target === "bag" && src === "box") actions?.take(current.buildingId, current.fid, from, to);
}

function cell(slot: InventorySlot | undefined, index: number, onClick: (() => void) | null, source: "box" | "bag"): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "gl-safe-cell" + (slot ? "" : " empty");
  if (slot) {
    el.appendChild(itemThumb(slot.itemId));
    if (slot.quantity > 1) { const q = document.createElement("span"); q.className = "q"; q.textContent = String(slot.quantity); el.appendChild(q); }
    if (onClick) el.addEventListener("click", onClick);
    el.draggable = true;
    el.addEventListener("dragstart", (e) => {
      e.dataTransfer?.setData("text/plain", `${source}:${slot.slotIndex}`);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    });
  }
  // Every cell (filled or empty) is a slot-to-slot drop target — matching the bag's behaviour.
  el.addEventListener("dragover", (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; el.classList.add("drop-hint"); });
  el.addEventListener("dragleave", () => el.classList.remove("drop-hint"));
  el.addEventListener("drop", (e) => {
    e.preventDefault();
    el.classList.remove("drop-hint");
    routeDrop(e.dataTransfer?.getData("text/plain") ?? "", source, index);
  });
  return el;
}

function renderGrid(grid: HTMLDivElement, count: number, cols: number, list: InventorySlot[], move: (i: number) => void, source: "box" | "bag"): void {
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  const by = new Map(list.map((s) => [s.slotIndex, s]));
  const cells: HTMLDivElement[] = [];
  for (let i = 0; i < count; i++) {
    const s = by.get(i);
    cells.push(cell(s, i, s ? () => move(i) : null, source));
  }
  grid.replaceChildren(...cells);
}

function refresh(): void {
  if (!current) return;
  const c = current;
  const bag = inventoryStore.get();
  const kg = slots.reduce((n, s) => n + s.quantity, 0) * ITEM_WEIGHT_KG;
  if (boxTitle) boxTitle.textContent = `SAFEBOX  ${slots.length}/${SAFEBOX_SLOTS}`;
  if (kgEl) kgEl.textContent = `${kg}/${SAFEBOX_CAPACITY_KG} KG`;
  if (boxGrid) renderGrid(boxGrid, SAFEBOX_SLOTS, INVENTORY_COLS, slots, (i) => actions?.take(c.buildingId, c.fid, i), "box");
  if (bagGrid) renderGrid(bagGrid, 20, INVENTORY_COLS, bag, (i) => actions?.store(c.buildingId, c.fid, i), "bag");
}

/** Server pushed a safebox snapshot — apply it if it's the box we have open. */
export function setSafeboxSnapshot(buildingId: string, fid: string, next: InventorySlot[]): void {
  if (!current || current.buildingId !== buildingId || current.fid !== fid) return;
  slots = next;
  refresh();
}

function ensure(): void {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-safe-overlay";
  overlay.innerHTML = `
    <div id="gl-safe-card">
      <h1>SAFEBOX</h1>
      <div class="hint">shared with this property's owner, staff, and key holders</div>
      <div class="gl-safe-cols">
        <div class="gl-safe-col"><h2 id="gl-safe-btitle">SAFEBOX</h2><div class="gl-safe-grid" id="gl-safe-box"></div></div>
        <div class="gl-safe-col"><h2>YOUR BAG</h2><div class="gl-safe-grid" id="gl-safe-bag"></div></div>
      </div>
      <div id="gl-safe-bar"><div id="gl-safe-kg">0/${SAFEBOX_CAPACITY_KG} KG</div></div>
      <button id="gl-safe-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  boxGrid = overlay.querySelector("#gl-safe-box");
  bagGrid = overlay.querySelector("#gl-safe-bag");
  boxTitle = overlay.querySelector("#gl-safe-btitle");
  kgEl = overlay.querySelector("#gl-safe-kg");
  overlay.querySelector("#gl-safe-close")!.addEventListener("click", closeSafebox);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeSafebox(); });
}

export function openSafebox(buildingId: string, fid: string): void {
  ensure();
  current = { buildingId, fid };
  slots = [];
  overlay!.classList.add("open");
  unsub = [inventoryStore.subscribe(refresh)];
  refresh();
  actions?.request(buildingId, fid);
}

export function closeSafebox(): void {
  overlay?.classList.remove("open");
  current = null;
  unsub.forEach((fn) => fn()); unsub = [];
}

export function isSafeboxOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
